import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { StorytellerService } from '../../services';
import { queryKeys } from '../keys';
import type {
  AddSectionRequest,
  CreateStoryConceptRequest,
  GenerateOutlineRequest,
  ReplaceOutlineRequest,
  StoryConceptDetail,
  UpdateSectionRequest,
  UpdateStoryConceptRequest,
} from '../../domain';

const STALE_MS = 30 * 1000;

export function useStorytellerConcepts() {
  return useQuery({
    queryKey: queryKeys.storytellerConcepts(),
    queryFn: () => StorytellerService.listConcepts(),
    staleTime: STALE_MS,
  });
}

export function useStorytellerConcept(conceptId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.storytellerConcept(conceptId ?? ''),
    queryFn: () => StorytellerService.getConcept(conceptId!),
    enabled: !!conceptId,
    staleTime: STALE_MS,
  });
}

export function useCreateConcept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateStoryConceptRequest) => StorytellerService.createConcept(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcepts() });
    },
  });
}

export function useUpdateConcept(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateStoryConceptRequest) =>
      StorytellerService.updateConcept(conceptId, req),
    onSuccess: detail => {
      qc.setQueryData(queryKeys.storytellerConcept(conceptId), detail);
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcepts() });
    },
  });
}

export function useDeleteConcept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conceptId: string) => StorytellerService.deleteConcept(conceptId),
    onSuccess: (_void, conceptId) => {
      qc.removeQueries({ queryKey: queryKeys.storytellerConcept(conceptId) });
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcepts() });
    },
  });
}

/** Helper used by the outline + section mutations: cache the returned detail. */
function cacheDetail(
  qc: ReturnType<typeof useQueryClient>,
  conceptId: string,
  detail: StoryConceptDetail,
) {
  qc.setQueryData(queryKeys.storytellerConcept(conceptId), detail);
}

export function useGenerateOutline(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: GenerateOutlineRequest = {}) =>
      StorytellerService.generateOutline(conceptId, req),
    onSuccess: detail => cacheDetail(qc, conceptId, detail),
  });
}

export function useReplaceOutline(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ReplaceOutlineRequest) => StorytellerService.replaceOutline(conceptId, req),
    onSuccess: detail => cacheDetail(qc, conceptId, detail),
  });
}

export function useAddSection(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: AddSectionRequest) => StorytellerService.addSection(conceptId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcept(conceptId) });
    },
  });
}

export function useUpdateSection(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, patch }: { sectionId: string; patch: UpdateSectionRequest }) =>
      StorytellerService.updateSection(sectionId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcept(conceptId) });
    },
  });
}

export function useDeleteSection(conceptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => StorytellerService.deleteSection(sectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcept(conceptId) });
    },
  });
}
